import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["lesson", "onFinish"];
import PortalIdParser from 'PortalIdParser';
import Raven from 'raven-js';
import { useMemo } from 'react';
import { usePracticalExerciseStore } from 'academy-common-lib/features/practicalExercises/practicalExerciseStore';
import { unwrapChirpError } from 'academy-common-lib/utils/DataFetchingClient';
import { usePlaylistIdFromSearch } from '../../playlistContent/hooks';
import CompletedPracticalExerciseCondensed from './CompletedPracticalExerciseCondensed';
import FailedPracticalExerciseCondensed from './FailedPracticalExerciseCondensed';
import getLang from '../../../utils/getLang';
import InProgressPracticalExercise from './InProgressPracticalExercise';
import InReviewPracticalExerciseCondensed from './InReviewPracticalExerciseCondensed';
import NotStartedPracticalExercise from './NotStartedPracticalExercise';
import PracticalExerciseError from './PracticalExerciseError';
import PracticalExerciseLoading from './PracticalExerciseLoading';
import PracticalExerciseRequiredAccess from './PracticalExerciseRequiredAccess';
import PracticalExerciseRequiredProduct from './PracticalExerciseRequiredProduct';
import PracticalExerciseWithSubmission from './PracticalExerciseWithSubmission';
import UsageTracker from '../../../utils/UsageTracker';
import { useDeletePracticalExerciseProgress, useIsSimulationExercise, usePracticalExerciseRequirements, useSetManuallyGradedPracticalExerciseWithAssetSubmitted, useSetPracticalExerciseStarted } from '../practicalExerciseQueries';
import { ContentProgressTypes, PracticalExerciseTypes } from '../../../constants/ContentConstants';
import { isPracticalExerciseAsset } from '../../tracks/tracksUtils';
import { jsx as _jsx } from "react/jsx-runtime";
export const WEBPACK_3_FORCE_MODULE_IMPORT = 1;
export const PracticalExercise = ({
  content,
  lesson,
  track,
  contentProgress,
  onFinish
}) => {
  var _chirpExercise$exerci, _chirpExercise$tracke;
  const contentId = content.id;
  const lessonId = lesson.id;
  const trackId = track === null || track === void 0 ? void 0 : track.id;
  const trackName = track === null || track === void 0 ? void 0 : track.name;
  const lessonName = lesson.name;
  const contentStatus = (contentProgress === null || contentProgress === void 0 ? void 0 : contentProgress.progressType) || 'NOT_STARTED';
  const practicalExercise = useMemo(() => {
    if (!lesson || !lesson.content) {
      return null;
    }
    const foundExercise = lesson.content.find(c => c.id === content.id);
    return foundExercise;
  }, [content.id, lesson]);
  const {
    practicalExerciseRequirements,
    practicalExerciseRequirementsLoading,
    practicalExerciseRequirementsError
  } = usePracticalExerciseRequirements({
    contentId
  });
  const chirpExercise = practicalExercise && isPracticalExerciseAsset(practicalExercise) ? practicalExercise : null;
  const isManuallyGraded = chirpExercise === null || chirpExercise === void 0 ? void 0 : chirpExercise.manuallyApproved;
  const exerciseLocation = (_chirpExercise$exerci = chirpExercise === null || chirpExercise === void 0 ? void 0 : chirpExercise.exerciseLocation) !== null && _chirpExercise$exerci !== void 0 ? _chirpExercise$exerci : '';
  const trackerNotRequired = (_chirpExercise$tracke = chirpExercise === null || chirpExercise === void 0 ? void 0 : chirpExercise.trackerNotRequired) !== null && _chirpExercise$tracke !== void 0 ? _chirpExercise$tracke : false;
  const practicalExerciseType = chirpExercise === null || chirpExercise === void 0 ? void 0 : chirpExercise.type;
  const [startPracticalExercise] = useSetPracticalExerciseStarted();
  const [submitPracticalExerciseWithAsset] = useSetManuallyGradedPracticalExerciseWithAssetSubmitted();
  const [deletePracticalExerciseProgress] = useDeletePracticalExerciseProgress();
  const {
    isSimulationExercise
  } = useIsSimulationExercise(contentId, trackId);
  const {
    startExerciseContext
  } = usePracticalExerciseStore();
  const playlistId = usePlaylistIdFromSearch();
  const trackExercise = action => {
    const graded = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.graded;
    const name = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name;
    const requiredProduct = practicalExerciseRequirements && practicalExerciseRequirements.requiredProducts && practicalExerciseRequirements.requiredProducts[0];
    const trackingMethod = trackerNotRequired ? UsageTracker.track : UsageTracker.trackExternalLink;
    trackingMethod.call(UsageTracker, 'contentInteraction', {
      action,
      contentId,
      contentLanguage: practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.language,
      contentType: 'practical-exercise',
      trackId,
      trackName,
      lessonId,
      lessonName,
      contentName: name,
      productName: requiredProduct || '',
      isUntrackedExercise: trackerNotRequired,
      graded: !!graded,
      manuallyApproved: isManuallyGraded
    });
  };
  const handleClickThroughToProductPage = () => trackExercise('click-to-product-page');
  const setExerciseContext = simulationPortalId => {
    if (!practicalExercise) {
      return;
    }
    startExerciseContext({
      name: practicalExercise.name,
      description: practicalExercise.description,
      id: Number(contentId),
      exerciseLocation,
      lessonId,
      trackId,
      portalId: simulationPortalId || PortalIdParser.getPortalIdFromPath(),
      sourcePortalId: PortalIdParser.getPortalIdFromPath(),
      isSimulation: isSimulationExercise,
      isGraded: practicalExercise.graded,
      isManuallyGraded,
      playlistId
    });
  };
  const handleStartExercise = async simulationPortalId => {
    setExerciseContext(simulationPortalId);
    trackExercise('click-start-exercise');
    await startPracticalExercise(lessonId, contentId, trackId, simulationPortalId).catch(Raven.captureException.bind(Raven));
  };
  const handleTryExerciseAgain = simulationPortalId => {
    setExerciseContext(simulationPortalId);
    trackExercise('click-revisit-exercise');
  };
  const handleResumeExercise = simulationPortalId => {
    setExerciseContext(simulationPortalId);
    trackExercise('click-resume-exercise');
  };
  const handleSubmitManualExercise = (assetType, reference) => {
    trackExercise('click-review-exercise');
    submitPracticalExerciseWithAsset({
      contentId,
      assetType,
      reference,
      lessonId,
      trackId
    }).catch(Raven.captureException);
  };
  const handleClickResubmit = () => {
    trackExercise('retry-practical-exercise');
    deletePracticalExerciseProgress({
      contentId,
      lessonId,
      trackId
    }).catch(Raven.captureException.bind(Raven));
  };
  const requiredProduct = practicalExerciseRequirements === null || practicalExerciseRequirements === void 0 ? void 0 : practicalExerciseRequirements.requiredProducts[0];
  const requiredAccess = practicalExerciseRequirements === null || practicalExerciseRequirements === void 0 ? void 0 : practicalExerciseRequirements.requiredAccess[0];
  const error = practicalExerciseRequirementsError ? unwrapChirpError(practicalExerciseRequirementsError) : undefined;
  const loading = practicalExerciseRequirementsLoading;
  if (loading) {
    return /*#__PURE__*/_jsx(PracticalExerciseLoading, {});
  }
  if (error) {
    return /*#__PURE__*/_jsx(PracticalExerciseError, {
      error: error
    });
  }
  if (contentStatus === ContentProgressTypes.COMPLETED) {
    return /*#__PURE__*/_jsx(CompletedPracticalExerciseCondensed, {
      advanceToNextContent: onFinish,
      allowRevisitExercise: practicalExerciseType === 'AUTOMATICALLY_GRADED',
      contentId: Number(contentId),
      exerciseLocation: exerciseLocation,
      handleSetExerciseContext: handleTryExerciseAgain,
      language: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.language) || lesson.language,
      lessonId: lessonId,
      trackId: trackId
    });
  }
  if (contentStatus === ContentProgressTypes.IN_REVIEW) {
    return /*#__PURE__*/_jsx(InReviewPracticalExerciseCondensed, {
      advanceToNextContent: onFinish,
      contentId: contentId,
      isManual: isManuallyGraded,
      isPassRequiredForCert: !!(practicalExercise !== null && practicalExercise !== void 0 && practicalExercise.graded),
      lessonId: lessonId,
      onResubmitClick: handleClickResubmit,
      resubmitType: "resubmit",
      title: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name) || '',
      trackId: trackId
    });
  }
  if (contentStatus === ContentProgressTypes.STARTED && practicalExerciseType !== PracticalExerciseTypes.MANUALLY_GRADED_WITH_FILE_SUBMISSION) {
    return /*#__PURE__*/_jsx(InProgressPracticalExercise, {
      advanceToNextContent: onFinish,
      contentId: Number(contentId),
      exercise: practicalExercise,
      exerciseLocation: exerciseLocation,
      language: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.language) || getLang(),
      lessonId: lessonId,
      onResumeExercise: handleResumeExercise,
      trackId: trackId
    });
  }
  if (contentStatus === ContentProgressTypes.FAILED) {
    return /*#__PURE__*/_jsx(FailedPracticalExerciseCondensed, {
      advanceToNextContent: onFinish,
      contentId: Number(contentId),
      isPassRequiredForCert: !!(practicalExercise !== null && practicalExercise !== void 0 && practicalExercise.graded),
      lessonId: lessonId,
      onResubmitClick: handleClickResubmit,
      resubmitType: "resubmit",
      title: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name) || '',
      trackId: trackId
    });
  }
  if (requiredProduct) {
    return /*#__PURE__*/_jsx(PracticalExerciseRequiredProduct, {
      advanceToNextContent: onFinish,
      onClickProductLink: handleClickThroughToProductPage,
      portalId: PortalIdParser.getPortalIdFromPath(),
      requiredProduct: requiredProduct
    });
  }
  if (requiredAccess) {
    return /*#__PURE__*/_jsx(PracticalExerciseRequiredAccess, {
      advanceToNextContent: onFinish,
      product: requiredAccess
    });
  }
  if (practicalExerciseType === PracticalExerciseTypes.MANUALLY_GRADED_WITH_FILE_SUBMISSION) {
    return /*#__PURE__*/_jsx(PracticalExerciseWithSubmission, {
      advanceToNextContent: onFinish,
      isPassRequiredForCert: !!(practicalExercise !== null && practicalExercise !== void 0 && practicalExercise.graded),
      isSubmitting: false,
      onSubmit: handleSubmitManualExercise,
      title: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name) || ''
    });
  }
  return practicalExercise && /*#__PURE__*/_jsx(NotStartedPracticalExercise, {
    advanceToNextContent: onFinish,
    contentId: contentId,
    exercise: practicalExercise,
    exerciseLocation: exerciseLocation,
    isSimulationExercise: isSimulationExercise,
    language: practicalExercise.language,
    lessonId: lessonId,
    onStartClick: handleStartExercise,
    trackId: trackId
  });
};
export const PracticalExerciseContainer = _ref => {
  let {
      lesson,
      onFinish
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  return lesson && onFinish ? /*#__PURE__*/_jsx(PracticalExercise, Object.assign({
    lesson: lesson,
    onFinish: onFinish
  }, props)) : null;
};
export default PracticalExerciseContainer;